/**
 * Provider connections: registration, verification, the lifecycle verbs and
 * the inventory a connection's provider lists.
 *
 * The viewer read and the administrator read are separate calls against
 * separate routes rather than one call with a flag, because that is how the
 * boundary is drawn: only `/admin/` returns the account reference, the
 * resolved endpoint and the secret's name, and only an organisation
 * administrator may reach it. A client that merged them would decide the
 * gate for itself.
 */

import type {
  Connection,
  ConnectionAdmin,
  ConnectionCreate,
  ConnectionFilters,
  ConnectionInventory,
  ConnectionListResponse,
  ConnectionUpdate,
  ConnectionVerification,
  InventoryPage,
  ProviderTypeListResponse,
} from '@/types/modelCapabilities'
import { apiClient, serverBudgetTimeout } from './client'
import { ifMatchHeaders } from './ifMatch'
import { BASE } from './modelCapabilityPaths'

export const modelCapabilityConnections = {
  listConnections: async (
    filters: ConnectionFilters = {}
  ): Promise<ConnectionListResponse> => {
    const response = await apiClient.get<ConnectionListResponse>(
      `${BASE}/connections/`,
      { params: filters }
    )
    return response.data
  },

  getConnection: async (connectionId: string): Promise<Connection> => {
    const response = await apiClient.get<Connection>(
      `${BASE}/connections/${connectionId}/`
    )
    return response.data
  },

  getConnectionAdmin: async (
    connectionId: string
  ): Promise<ConnectionAdmin> => {
    const response = await apiClient.get<ConnectionAdmin>(
      `${BASE}/connections/${connectionId}/admin/`
    )
    return response.data
  },

  // What each provider needs stated, served rather than restated in
  // TypeScript: a second copy of the matrix here would be a form that accepts
  // what the boundary refuses.
  listProviderTypes: async (): Promise<ProviderTypeListResponse> => {
    const response = await apiClient.get<ProviderTypeListResponse>(
      `${BASE}/connections/provider-types/`
    )
    return response.data
  },

  // 201, answering with the administrator's projection: the wizard's next step
  // needs the id and the version it must verify against.
  registerConnection: async (
    body: ConnectionCreate
  ): Promise<ConnectionAdmin> => {
    const response = await apiClient.post<ConnectionAdmin>(
      `${BASE}/connections/`,
      body
    )
    return response.data
  },

  updateConnection: async (
    connectionId: string,
    body: ConnectionUpdate,
    ifMatch: number
  ): Promise<ConnectionAdmin> => {
    const response = await apiClient.patch<ConnectionAdmin>(
      `${BASE}/connections/${connectionId}/`,
      body,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  // A provider failure is an outcome in the 200 body, not a rejection: this
  // call exists to report what the provider said. The version travels with the
  // answer, so the *Enable* that follows needs no second read.
  verifyConnection: async (
    connectionId: string,
    budgetSeconds: number
  ): Promise<ConnectionVerification> => {
    const response = await apiClient.post<ConnectionVerification>(
      `${BASE}/connections/${connectionId}/verify/`,
      undefined,
      serverBudgetTimeout(budgetSeconds)
    )
    return response.data
  },

  enableConnection: async (
    connectionId: string,
    ifMatch: number
  ): Promise<ConnectionAdmin> => {
    const response = await apiClient.post<ConnectionAdmin>(
      `${BASE}/connections/${connectionId}/enable/`,
      undefined,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  disableConnection: async (
    connectionId: string,
    ifMatch: number
  ): Promise<ConnectionAdmin> => {
    const response = await apiClient.post<ConnectionAdmin>(
      `${BASE}/connections/${connectionId}/disable/`,
      undefined,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  decommissionConnection: async (
    connectionId: string,
    ifMatch: number
  ): Promise<ConnectionAdmin> => {
    const response = await apiClient.post<ConnectionAdmin>(
      `${BASE}/connections/${connectionId}/decommission/`,
      undefined,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  getConnectionInventory: async (
    connectionId: string,
    page: InventoryPage = {}
  ): Promise<ConnectionInventory> => {
    const response = await apiClient.get<ConnectionInventory>(
      `${BASE}/connections/${connectionId}/inventory/`,
      { params: page }
    )
    return response.data
  },

  // Answers with the refreshed board rather than an acknowledgement, so the
  // page the operator is looking at is the page the provider just described.
  refreshConnectionInventory: async (
    connectionId: string,
    budgetSeconds: number,
    page: InventoryPage = {}
  ): Promise<ConnectionInventory> => {
    const response = await apiClient.post<ConnectionInventory>(
      `${BASE}/connections/${connectionId}/inventory/refresh/`,
      undefined,
      { params: page, ...serverBudgetTimeout(budgetSeconds) }
    )
    return response.data
  },
}
